import React, { useState, useEffect } from 'react';
import { useAppStore } from '@/lib/store';
import { formatIST } from '@/utils/format';

function scheduleNotification(event) {
  if (!('Notification' in window)) return;
  const notifyAt = event.startTime - 15 * 60 * 1000;
  const delay = notifyAt - Date.now();
  if (delay > 0 && delay < 7 * 24 * 60 * 60 * 1000) {
    setTimeout(() => {
      Notification.requestPermission().then((perm) => {
        if (perm === 'granted') {
          new Notification(`Reminder: ${event.title}`, {
            body: 'Starts in 15 minutes',
            icon: '/favicon.ico',
          });
        }
      });
    }, delay);
  }
}

export default function CalendarView() {
  const { selectedDate, setSelectedDate, calendarEvents } = useAppStore();
  const [selectedEvent, setSelectedEvent] = useState(null);

  // Schedule browser notifications for all upcoming events on mount
  useEffect(() => {
    calendarEvents.forEach(scheduleNotification);
  }, [calendarEvents]);
  const [currentDate, setCurrentDate] = useState(new Date());

  useEffect(() => {
    if (selectedDate) setCurrentDate(new Date(selectedDate));
  }, [selectedDate]);

  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const blanks = Array.from({ length: firstDayOfMonth }, (_, i) => i);
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const isSelected = (day) => {
    if (!selectedDate) return false;
    const d = new Date(selectedDate);
    return d.getDate() === day && d.getMonth() === currentDate.getMonth() && d.getFullYear() === currentDate.getFullYear();
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day && today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();
  };

  const getEventsForDay = (day) => {
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), day).setHours(0, 0, 0, 0);
    const end = start + 86400000;
    return calendarEvents.filter(e => e.startTime >= start && e.startTime < end);
  };

  const selectedDayEvents = selectedDate ? (() => {
    const d = new Date(selectedDate);
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).setHours(0, 0, 0, 0);
    const end = start + 86400000;
    return calendarEvents.filter(e => e.startTime >= start && e.startTime < end);
  })() : [];

  return (
    <div className="flex-1 p-4 md:p-6 w-full h-full flex flex-col md:flex-row gap-4">
      {/* Calendar Grid */}
      <div className="bg-[#323335] border border-[#515153] rounded-[6px] p-4 md:p-6 flex-1 shadow-lg flex flex-col min-w-[300px] overflow-x-auto">
        <div className="flex justify-between items-center mb-6">
          <button onClick={prevMonth} className="p-2 hover:bg-[#3e3f42] rounded text-[#b3b4b7] transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div className="text-white font-bold text-[16px] md:text-[20px] tracking-wide text-center">
            {months[currentDate.getMonth()]} {currentDate.getFullYear()}
          </div>
          <button onClick={nextMonth} className="p-2 hover:bg-[#3e3f42] rounded text-[#b3b4b7] transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
          </button>
        </div>

        <div className="grid grid-cols-7 gap-1 md:gap-2 text-center mb-2 min-w-[300px]">
          {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => (
            <div key={d} className="text-[#8f9196] text-[12px] md:text-[14px] font-medium py-1 md:py-2">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 md:gap-2 text-center flex-1 min-w-[300px]">
          {blanks.map(b => <div key={`blank-${b}`} className="min-h-[60px] md:min-h-[80px] w-full border border-transparent" />)}
          {days.map(day => {
            const dayEvents = getEventsForDay(day);
            return (
              <button
                key={day}
                onClick={() => setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
                className={`min-h-[60px] md:min-h-[80px] w-full rounded-[4px] text-[14px] md:text-[16px] flex flex-col items-end p-2 md:p-3 transition-colors border relative
                  ${isSelected(day) ? 'bg-[#6498CC] bg-opacity-20 border-[#6498CC] text-[#6498CC] font-bold' :
                    isToday(day) ? 'border-[#6498CC] text-[#6498CC] font-bold' :
                    'border-[#3e3f42] text-[#d5d6d9] hover:bg-[#3e3f42]'}`}
              >
                <span>{day}</span>
                {dayEvents.length > 0 && (
                  <div className="flex gap-0.5 mt-1 flex-wrap">
                    {dayEvents.slice(0, 3).map((_, i) => (
                      <div key={i} className="w-1.5 h-1.5 rounded-full bg-[#6498CC]" />
                    ))}
                    {dayEvents.length > 3 && (
                      <span className="text-[9px] text-[#8f9196]">+{dayEvents.length - 3}</span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 pt-4 border-t border-[#515153] flex flex-col md:flex-row justify-between items-center gap-3">
          <button onClick={() => { setSelectedDate(new Date()); setCurrentDate(new Date()); }} className="text-[14px] font-medium text-[#6498CC] hover:text-white transition-colors px-4 py-2 hover:bg-[#3e3f42] rounded w-full md:w-auto">Today</button>
          {selectedDate && (
            <div className="text-[#d5d6d9] text-[13px] md:text-[14px] text-center">
              Selected: <strong className="text-white ml-1">{new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</strong>
            </div>
          )}
          <button onClick={() => setSelectedDate(null)} className="text-[14px] font-medium text-[#8f9196] hover:text-white transition-colors px-4 py-2 hover:bg-[#3e3f42] rounded w-full md:w-auto">Clear</button>
        </div>
      </div>

      {/* Events Sidebar */}
      <div className="w-full md:w-[300px] bg-[#323335] border border-[#515153] rounded-[6px] p-4 flex-shrink-0 overflow-y-auto">
        <h3 className="text-white font-bold text-[15px] mb-4">
          {selectedDate ? new Date(selectedDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' Events' : 'All Events'}
        </h3>

        {(selectedDate ? selectedDayEvents : calendarEvents).length === 0 ? (
          <div className="text-[#8f9196] text-[13px] text-center py-8">
            {selectedDate ? 'No events for this day' : 'No events yet'}
            <p className="mt-2 text-[12px]">Chat with Palinos to schedule events naturally.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {(selectedDate ? selectedDayEvents : calendarEvents.slice(0, 20)).map(evt => (
              <div
                key={evt.id || evt.title + evt.startTime}
                onClick={() => setSelectedEvent(selectedEvent?.id === evt.id ? null : evt)}
                className={`bg-[#232426] border rounded-md p-3 cursor-pointer transition-all hover:border-[#6498CC] ${selectedEvent?.id === evt.id ? 'border-[#6498CC] bg-[#1e3a5f]' : 'border-[#515153]'}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="text-white text-[13px] font-medium flex-1">{evt.title}</div>
                  <span className="text-[#6498CC] text-[10px] flex-shrink-0 mt-0.5">{selectedEvent?.id === evt.id ? '▲' : '▼'}</span>
                </div>
                <div className="text-[#8f9196] text-[12px] mt-1 flex items-center gap-1.5">
                  <svg className="w-3 h-3 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  {formatIST(evt.startTime)}
                </div>
                {selectedEvent?.id === evt.id && (
                  <div className="mt-2 pt-2 border-t border-[#515153]">
                    {evt.description && evt.description !== evt.title && (
                      <div className="text-[#b3b4b7] text-[12px] mb-2">{evt.description}</div>
                    )}
                    <div className="text-[#8f9196] text-[11px] space-y-0.5">
                      <div>📅 {new Date(evt.startTime).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</div>
                      <div>🕐 {new Date(evt.startTime).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
